import { compose, createStore, applyMiddleware, Middleware } from 'redux';
import { persistStore, persistReducer, PersistConfig } from 'redux-persist';
import createMigrate from 'redux-persist/lib/createMigrate';
import storage from 'redux-persist/lib/storage';
import logger from 'redux-logger';
import createSagaMiddleware from 'redux-saga';

import { rootSaga } from './rootSaga';

import { rootReducer, RootState } from './rootReducer';

export type { RootState };

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

type ExtendedPersistConfig = PersistConfig<RootState> & {
  whitelist: (keyof RootState)[];
};

/**
 * Bump PERSIST_VERSION whenever a slice's persisted shape changes in a way
 * that older clients could choke on. Each new version should add a migration
 * below describing how to bring the old state forward (or drop it).
 */
const PERSIST_VERSION = 1;

const migrations = {
  // v1: first explicit version. Drop the persisted `settings` slice — its
  // shape has evolved across deploys and stale data has caused white-screen
  // crashes on render. Auth is preserved so users stay logged in.
  1: (state: any) => {
    if (!state) return state;
    return {
      ...state,
      settings: undefined,
    };
  },
};

const persistConfig: ExtendedPersistConfig = {
  key: 'root',
  version: PERSIST_VERSION,
  storage,
  whitelist: ['auth', 'settings'],
  migrate: createMigrate(migrations, { debug: false }),
};

const sagaMiddleware = createSagaMiddleware();

const persistedReducer = persistReducer(persistConfig, rootReducer);

const middleWares = [process.env.NODE_ENV !== 'production' && logger, sagaMiddleware].filter(
  (middleware): middleware is Middleware => Boolean(middleware),
);

const composeEnhancer =
  (process.env.NODE_ENV !== 'production' &&
    window &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

const composedEnhancers = composeEnhancer(applyMiddleware(...middleWares));

export const store = createStore(persistedReducer, undefined, composedEnhancers);

sagaMiddleware.run(rootSaga);

// A portal deep-link carrying forceLogin=1 must start from a clean slate. Clear
// the persisted customer synchronously HERE, before persistStore rehydrates it,
// otherwise the old auth is read back into the store and re-written to storage.
const forceLoginParams = new URLSearchParams(window.location.search);
if (forceLoginParams.get('sessionToken') && forceLoginParams.get('forceLogin') === '1') {
  localStorage.removeItem('persist:root');
  localStorage.removeItem('auth.sessionToken');
  localStorage.removeItem('auth.sessionTokenTimestamp');
}

export const persistor = persistStore(store);
